import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Modal from '../../components/modal/Modal';
import { Col, convertToKRW, Row } from '../../lib/utils';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import { EtcBonusAmountType } from '../../api/salaryCalc';

export type EtcBonusValue = {
    name: string;
    amount: number;
    amountType: EtcBonusAmountType;
    vatIncluded: boolean;
};

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    selectedEtcBonus?: EtcBonusValue;
    onClickConfirm?: (etc: EtcBonusValue, type: 'new' | 'update') => void;
    // 센터 기준 매출 및 VAT 정보 (percentage 수당 미리보기용)
    settlement: number;
    vatPolicy: 'inclusive' | 'exclusive';
    vatRate: number;
};

const ModalAddEtcSalary = ({
    setIsVisible,
    selectedEtcBonus,
    onClickConfirm,
    settlement,
    vatPolicy,
    vatRate,
}: Props) => {
    const [etcName, setEtcName] = useState(selectedEtcBonus?.name || '기타 수당');
    // 금액 입력은 string 으로 관리 (소수점 부분 입력 중 "3." 같은 상태 허용)
    const [etcAmountInput, setEtcAmountInput] = useState<string>(
        selectedEtcBonus?.amount !== undefined && selectedEtcBonus.amount !== 0
            ? String(selectedEtcBonus.amount)
            : '',
    );
    const [amountType, setAmountType] = useState<EtcBonusAmountType>(
        selectedEtcBonus?.amountType ?? 'fixed',
    );
    const [vatIncluded, setVatIncluded] = useState<boolean>(selectedEtcBonus?.vatIncluded ?? true);

    // name 포커싱 여부
    const [isNameFocused, setIsNameFocused] = useState(false);
    // amount 포커싱 여부
    const [isAmountFocused, setIsAmountFocused] = useState(false);

    // disable
    const [isDisabled, setIsDisabled] = useState(true);

    const isPercentage = amountType === 'percentage';

    // 유효한 숫자 문자열 (빈 문자열이나 "3." 같은 미완성 값 제외)
    // - percentage : 소수점 허용
    // - fixed      : 정수만 허용 (소수점이 섞여 있으면 버림 처리)
    const parsedAmount = React.useMemo(() => {
        if (etcAmountInput === '') return NaN;
        if (amountType === 'percentage') {
            if (!/^-?\d+(\.\d+)?$/.test(etcAmountInput)) return NaN;
            return parseFloat(etcAmountInput);
        }
        // fixed: 정수 표현만 인정, 혹시 소수점 있으면 버림
        if (!/^-?\d+(\.\d+)?$/.test(etcAmountInput)) return NaN;
        return Math.floor(parseFloat(etcAmountInput));
    }, [etcAmountInput, amountType]);

    // VAT 반영 기준값 계산 (settlement + vatPolicy + vatRate → 포함/제외 환산)
    const vatIncludedBasis = React.useMemo(
        () =>
            vatPolicy === 'inclusive'
                ? settlement
                : Math.floor(settlement * (1 + vatRate / 100)),
        [settlement, vatPolicy, vatRate],
    );
    const vatExcludedBasis = React.useMemo(
        () =>
            vatPolicy === 'exclusive'
                ? settlement
                : Math.floor(settlement / (1 + vatRate / 100)),
        [settlement, vatPolicy, vatRate],
    );
    const effectiveBasis = vatIncluded ? vatIncludedBasis : vatExcludedBasis;
    const previewAmount = React.useMemo(() => {
        if (!isPercentage) return 0;
        const ratio = Number.isNaN(parsedAmount) ? 0 : parsedAmount;
        return Math.floor(effectiveBasis * (ratio / 100));
    }, [effectiveBasis, parsedAmount, isPercentage]);

    useEffect(() => {
        if (!etcName || Number.isNaN(parsedAmount)) {
            setIsDisabled(true);
            return;
        }
        if (isPercentage && (parsedAmount < -100 || parsedAmount > 100)) {
            setIsDisabled(true);
            return;
        }
        setIsDisabled(false);
    }, [etcName, parsedAmount, isPercentage]);
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !isDisabled && onClickConfirm) {
                setIsVisible(false);
                onClickConfirm(
                    { name: etcName, amount: parsedAmount, amountType, vatIncluded },
                    selectedEtcBonus ? 'update' : 'new',
                );
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        isDisabled,
        etcName,
        parsedAmount,
        amountType,
        vatIncluded,
        onClickConfirm,
        selectedEtcBonus,
        setIsVisible,
    ]);

    // input 포커싱
    useEffect(() => {
        etcNameRef.current?.focus();
    }, []);

    const etcNameRef = React.useRef<HTMLInputElement>(null);
    const etcAmountRef = React.useRef<HTMLInputElement>(null);
    return (
        <Modal setIsVisible={setIsVisible} whiteViewWidth={520} borderRadius={15}>
            <Col className="py-[36px] px-[33px]">
                <MediumText
                    fontSize={22}
                    lineHeight={38}
                    letterSpacing={-0.32}
                    color={colors.Text_1}
                    className="mb-[27px]"
                >
                    기타 수당 추가하기
                </MediumText>

                <Col className="mb-[24px]">
                    <RegularText
                        fontSize={16}
                        lineHeight={23}
                        letterSpacing={-0.32}
                        color={colors.Text_1}
                        className="mb-[9px]"
                    >
                        기타 수당 이름
                    </RegularText>
                    <input
                        ref={etcNameRef}
                        value={etcName}
                        onChange={(e) => setEtcName(e.target.value)}
                        className={`font-noto-demilight text-[17px] leading-[24px] text-[#3D4244] bg-trans  mr-[5px] border border-[#D9D9D9B2] p-[23px] rounded-[10px] hover:border-primary transition-colors duration-300 ease-in-out
                            ${isNameFocused ? 'border-primary' : ''}
                            `}
                        maxLength={15}
                        placeholder="기타 수당"
                        onFocus={() => setIsNameFocused(true)}
                        onBlur={() => setIsNameFocused(false)}
                    />
                </Col>
                <Col className="mb-[16px]">
                    <Row className="items-center justify-between mb-[9px]">
                        <RegularText
                            fontSize={16}
                            lineHeight={23}
                            letterSpacing={-0.32}
                            color={colors.Text_1}
                        >
                            기타 수당 금액
                        </RegularText>
                        {/* 원 / % 토글 */}
                        <Row className="bg-[#F4F5F7] rounded-[8px] p-[3px]">
                            <div
                                onClick={() => {
                                    setAmountType('fixed');
                                    setEtcAmountInput('');
                                }}
                                className={`px-[14px] h-[30px] flex items-center justify-center rounded-[6px] cursor-pointer ${
                                    !isPercentage ? 'bg-white shadow-sm' : ''
                                }`}
                            >
                                <RegularText
                                    fontSize={14}
                                    lineHeight={20}
                                    letterSpacing={-0.3}
                                    color={!isPercentage ? colors.Text_1 : '#6B7280'}
                                >
                                    원
                                </RegularText>
                            </div>
                            <div
                                onClick={() => {
                                    setAmountType('percentage');
                                    setEtcAmountInput('');
                                }}
                                className={`px-[14px] h-[30px] flex items-center justify-center rounded-[6px] cursor-pointer ${
                                    isPercentage ? 'bg-white shadow-sm' : ''
                                }`}
                            >
                                <RegularText
                                    fontSize={14}
                                    lineHeight={20}
                                    letterSpacing={-0.3}
                                    color={isPercentage ? colors.Text_1 : '#6B7280'}
                                >
                                    % (센터 매출 대비)
                                </RegularText>
                            </div>
                        </Row>
                    </Row>
                    <Row
                        className="w-full  border border-solid border-[#D9D9D9B2] rounded-[9px] p-[23px] justify-between mr-[20px] cursor-text hover:border-primary transition-colors duration-300 ease-in-out"
                        onClick={() => etcAmountRef.current?.focus()}
                    >
                        <input
                            ref={etcAmountRef}
                            value={etcAmountInput}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                    setEtcAmountInput('');
                                    return;
                                }
                                if (isPercentage) {
                                    // percentage: 숫자 + 소수점 한 개 허용, -100~100 범위 (음수 차감 허용)
                                    if (!/^-?\d*(\.\d*)?$/.test(raw)) return;
                                    const v = parseFloat(raw);
                                    if (!Number.isNaN(v) && (v > 100 || v < -100)) return;
                                } else {
                                    // fixed: 정수만 허용 (소수점 입력 차단), 음수 차감 허용
                                    if (!/^-?\d*$/.test(raw)) return;
                                }
                                setEtcAmountInput(raw);
                            }}
                            onBlur={() => {
                                setIsAmountFocused(false);
                                // 블러 시 "3." 같은 미완성 값 정리 (percentage 한정)
                                if (etcAmountInput.endsWith('.')) {
                                    setEtcAmountInput(etcAmountInput.slice(0, -1));
                                }
                            }}
                            className={`font-noto-demilight text-[17px] leading-[24px] text-[#3D4244] bg-trans  mr-[5px] w-full
                                ${isAmountFocused ? 'border-primary' : ''}
                                `}
                            maxLength={isPercentage ? 6 : 15}
                            placeholder={
                                isPercentage
                                    ? '센터 매출 대비 비율을 입력해주세요 (0~100, 소수점 가능)'
                                    : '기타 수당 금액을 입력해주세요'
                            }
                            onFocus={() => setIsAmountFocused(true)}
                        />
                        <DemiLightText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#6B7280">
                            {isPercentage ? '%' : '원'}
                        </DemiLightText>
                    </Row>
                </Col>

                {/* percentage 전용: VAT 포함/제외 토글 + 실시간 미리보기 */}
                {isPercentage && (
                    <Col className="mb-[24px] bg-[#F6F8FA] rounded-[12px] px-[18px] py-[16px]">
                        <Row className="items-center justify-between mb-[12px]">
                            <RegularText
                                fontSize={14}
                                lineHeight={20}
                                letterSpacing={-0.3}
                                color={colors.Text_1}
                            >
                                매출 기준 (VAT 반영)
                            </RegularText>
                            <Row className="bg-white rounded-[8px] p-[3px] border border-solid border-[#E6E8EB]">
                                <div
                                    onClick={() => setVatIncluded(true)}
                                    className={`px-[12px] h-[26px] flex items-center justify-center rounded-[6px] cursor-pointer ${
                                        vatIncluded ? 'bg-[#3D73DD]' : ''
                                    }`}
                                >
                                    <RegularText
                                        fontSize={13}
                                        lineHeight={18}
                                        letterSpacing={-0.3}
                                        color={vatIncluded ? 'white' : '#6B7280'}
                                    >
                                        VAT 포함
                                    </RegularText>
                                </div>
                                <div
                                    onClick={() => setVatIncluded(false)}
                                    className={`px-[12px] h-[26px] flex items-center justify-center rounded-[6px] cursor-pointer ${
                                        !vatIncluded ? 'bg-[#3D73DD]' : ''
                                    }`}
                                >
                                    <RegularText
                                        fontSize={13}
                                        lineHeight={18}
                                        letterSpacing={-0.3}
                                        color={!vatIncluded ? 'white' : '#6B7280'}
                                    >
                                        VAT 제외
                                    </RegularText>
                                </div>
                            </Row>
                        </Row>
                        <Row className="justify-between items-center mb-[6px]">
                            <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.3} color="#5A6370">
                                센터 총 매출 ({vatIncluded ? 'VAT 포함' : 'VAT 제외'})
                            </DemiLightText>
                            <DemiLightText fontSize={14} lineHeight={20} letterSpacing={-0.3} color={colors.Text_1}>
                                {convertToKRW(effectiveBasis)} 원
                            </DemiLightText>
                        </Row>
                        <Row className="justify-between items-center">
                            <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.3} color="#5A6370">
                                {`× ${Number.isNaN(parsedAmount) ? 0 : parsedAmount}% = 환산 금액`}
                            </DemiLightText>
                            <MediumText fontSize={16} lineHeight={22} letterSpacing={-0.3} color="#3D73DD">
                                {convertToKRW(previewAmount)} 원
                            </MediumText>
                        </Row>
                        <DemiLightText
                            fontSize={13}
                            lineHeight={18}
                            letterSpacing={-0.3}
                            color="#6B7280"
                            className="mt-[8px]"
                        >
                            매출은 해당 월 최종 집계 기준으로 매달 자동 재계산됩니다.
                        </DemiLightText>
                    </Col>
                )}

                <Row className="items-center self-end">
                    <Col
                        className="bg-GREY w-[110px] h-[41px] justify-center items-center rounded-[9px] mr-[9px] cursor-pointer"
                        onClick={() => setIsVisible(false)}
                    >
                        <RegularText fontSize={16} lineHeight={24} letterSpacing={-0.32} color={colors.Text_1}>
                            취소
                        </RegularText>
                    </Col>
                    <Col
                        className={`bg-primary w-[110px] h-[41px] justify-center items-center rounded-[9px]
                            ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                        onClick={() => {
                            if (!isDisabled && onClickConfirm) {
                                setIsVisible(false);
                                onClickConfirm(
                                    { name: etcName, amount: parsedAmount, amountType, vatIncluded },
                                    selectedEtcBonus ? 'update' : 'new'
                                );
                            }
                        }}
                    >
                        <RegularText fontSize={16} lineHeight={24} letterSpacing={-0.32} color={colors.white}>
                            적용하기
                        </RegularText>
                    </Col>
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalAddEtcSalary;
